<template>
  <div class="page-content">
    <div class="payment">
      <div class="payment-header">
        <CustomIcon name="logo" class="payment-header-logo" />
        <h2 class="payment-title">
          {{
            onboardingData.name_input
              ? onboardingData.name_input + ", living"
              : "Living"
          }}
          life without limits starts today
        </h2>
      </div>
      <h3 class="payment-subtitle">Your program</h3>
      <ProgramSummary
        v-for="(phobia, index) in phobias"
        :key="`:id-${phobia.id}-${index}`"
        :title="phobia.phobia"
        :description="phobia.tagline"
        :icon="phobia.image"
      />
      <div class="icon-text-set">
        <IconText iconName="vr" text="Personalized VR exposure therapy plan" />
        <IconText
          iconName="reports"
          text="Develop skills through clinical psychoeducation"
        />
        <IconText
          v-if="onboardingData.choose_path === 'parent'"
          iconName="program"
          text="Track your real world progress"
        />
      </div>
      <LogoBanner :listOfIcons="featuredIcons" text="As featured in" />
      <SnackbarPrompt icon="done" variation="success"
        >All costs are covered, you will not be charged</SnackbarPrompt
      >
      <SnackbarPrompt v-if="errorMessage" icon="warning" variation="warning">{{
        errorMessage
      }}</SnackbarPrompt>
      <CustomButton
        :disabled="!this.formIsValid() || registerUserIsLoading"
        :onClick="(e) => handleAction(e)"
      >
        <span v-if="!registerUserIsLoading">Next</span>
        <LoadingIndicator v-else />
      </CustomButton>
      <p class="payment-text">
        By continuing, I agree to oVRcome’s
        <a :href="terms" target="_blank">Terms & Conditions</a> and
        <a :href="privacyPolicy" target="_blank">Privacy Policy</a>
      </p>
    </div>
    <div class="faq">
      <h3 class="payment-subtitle">How it works</h3>
      <div class="faq-step">
        <CustomIcon name="unlock" />
        <div class="faq-step-content">
          <h4 class="faq-step-content-title">Today</h4>
          <p class="faq-step-content-description">
            Get full access to oVRcome. Your VR headset will be shipped to you.
            Begin your daily audio lessons. Don’t wait for your headset!
          </p>
        </div>
      </div>
      <div class="faq-step">
        <CustomIcon name="vr" />
        <div class="faq-step-content">
          <h4 class="faq-step-content-title">5-14 days</h4>
          <p class="faq-step-content-description">
            Your VR headset arrives. Start your VR sessions.
          </p>
        </div>
      </div>
      <h3 class="payment-subtitle">FAQ's</h3>
      <div :class="getFaqItemClass('listItem1')">
        <div
          @click="(e) => handleTouchItem(e, 'listItem1')"
          class="faq-list-item-row"
        >
          Does the app work on all smartphones?
          <CustomIcon name="arrow" />
        </div>
        <p class="faq-list-item-slot">
          To confirm if your device is compatible with the oVRcome app, you can
          use this quick look-up tool:
          <a :href="deviceSearch" target="_blank">{{ deviceSearch }}</a>
        </p>
      </div>
      <div :class="getFaqItemClass('listItem2')">
        <div
          @click="(e) => handleTouchItem(e, 'listItem2')"
          class="faq-list-item-row"
        >
          Can I use VR headset with optical glasses?
          <CustomIcon name="arrow" />
        </div>
        <p class="faq-list-item-slot">
          Our VR headset can be worn by people who wear spectacles. It has focus
          adjustment and interpupillary distance wheels which can be adjusted
          for maximum visual comfort and to give you the best immersive virtual
          reality experience possible through a mobile VR headset. If you are
          shortsighted, however, try watching VR with and without reading
          glasses to see what works best. Even though it may feel like you are
          looking at a screen two inches away from your eyes, the actual image
          you see in the VR headset has a focal distance of about 6.5 ft/ 2 m in
          most modern VR headsets.
        </p>
      </div>
      <div :class="getFaqItemClass('listItem3')">
        <div
          @click="(e) => handleTouchItem(e, 'listItem3')"
          class="faq-list-item-row"
        >
          Will I get dizzy using the headset?
          <CustomIcon name="arrow" />
        </div>
        <p class="faq-list-item-slot">
          Extended use of VR can lead to virtual reality sickness which causes
          dizziness, disorientation, nausea, eye soreness, and trouble focusing.
          Please take a 10 to 15 minute break every half hour by taking off your
          headset and looking at objects far away to reduce eye strain, even if
          you don’t think you need it. If symptoms persist, do not try to “power
          through” the content. Stop using the headset and try again in 24
          hours.
        </p>
      </div>
      <div :class="getFaqItemClass('listItem4')">
        <div
          @click="(e) => handleTouchItem(e, 'listItem4')"
          class="faq-list-item-row"
        >
          Does the app work with the Oculus headset (or similar)?
          <CustomIcon name="arrow" />
        </div>
        <p class="faq-list-item-slot">
          While Oculus or Pico style headsets are not compatible with the
          oVRcome app yet, we are working on introducing this integration
          further down the line. We started off by making sure that the
          entry-level to access this product is as low as possible and no
          advanced equipment is required, but understand that some of our users
          already own a VR headset and would like to be able to use it. For now,
          the app is only compatible with a Google Cardboard-style headset (free
          with every subscription, but shipping costs may apply depending on the
          location).
        </p>
      </div>
    </div>
  </div>
</template>
<script>
import CustomIcon from "@/components/CustomIcon.vue";
import LogoBanner from "@/components/LogoBanner.vue";
import CustomButton from "@/components/CustomButton.vue";
import ProgramSummary from "@/components/ProgramSummary.vue";
import SnackbarPrompt from "@/components/SnackbarPrompt.vue";
import LoadingIndicator from "@/components/LoadingIndicator.vue";
import IconText from "@/components/IconText.vue";
import { mapGetters } from "vuex";
import {
  MODULES,
  PAYMENT_FORMS,
  EXTERNAL_ROUTES,
  PLAN_TYPES,
  PLAN_BILLING_UNITS,
} from "@/config";
import {
  getCategoryById,
  denormaliseOrderSummary,
  getCourseType,
} from "@/utils";
import { webPaymentPlanValidator } from "@/utils/form-validation/index";
import { getPlansParams } from "@/utils/get-plans-params";
import { pageInformationMixin } from "@/mixins/index.js";
import classnames from "classnames";
import * as queryString from "query-string";

export default {
  data() {
    return {
      phobias: [],
      featuredIcons: ["orgs/1-news", "orgs/news-hub", "orgs/uoo"],
      validator: webPaymentPlanValidator,
      terms: EXTERNAL_ROUTES.termsAndConditions,
      privacyPolicy: EXTERNAL_ROUTES.privacyPolicy,
      deviceSearch: EXTERNAL_ROUTES.deviceSearch,
      registerUserIsLoading: false,
      errorMessage: null,
      touched: {
        listItem1: false,
        listItem2: false,
        listItem3: false,
        listItem4: false,
      },
    };
  },
  props: {
    updateFormState: Function,
    action: Function,
    formData: Object,
  },
  components: {
    CustomIcon,
    LogoBanner,
    CustomButton,
    ProgramSummary,
    SnackbarPrompt,
    LoadingIndicator,
    IconText,
  },
  mixins: [pageInformationMixin],
  mounted() {
    const { dispatch } = this.$store;
    const { onboardingData } = this;

    if (!this.referrer.user_source) {
      const search = location.hash.replace("#/", "");
      const query = queryString.parse(search);

      dispatch(`${MODULES.APP}/getReferrerCode`, {
        referral_code: query.referral_code,
      });
    }

    const category = getCategoryById(onboardingData.choose_phobias);
    this.phobias = [category];

    this.setInitialSearchParams(() => {
      dispatch(`${MODULES.COUNTRY}/getCountriesAction`);

      dispatch(
        `${MODULES.PLAN}/getPlansAction`,
        getPlansParams({ onboardingData })
      ).then((plans) => {
        const planData = plans.find(
          (item) =>
            item?.type === PLAN_TYPES.globalPlan &&
            item?.billing_units === "yearly"
        );

        if (!planData) {
          this.setError(
            "This plan does not exist in our system. Please contact support@ovrcome.io"
          );
        }

        this.updateFormState(
          PAYMENT_FORMS.partner,
          "plan_id",
          planData.id,
          false
        );
      });
    });

    this.trackEmail();
  },
  computed: {
    ...mapGetters(MODULES.WELCOME, {
      onboardingData: "getOnboardingData",
    }),
    ...mapGetters(MODULES.PLAN, {
      allPlans: "getAllPlans",
    }),
    ...mapGetters(MODULES.APP, {
      referrer: "getReferrer",
    }),
  },
  methods: {
    setError(err) {
      this.errorMessage = err;
    },
    setInitialSearchParams(callback) {
      const search = location.hash.replace("#/", "");
      const whiteListedKeys = ["referral_code"];

      if (search) {
        const obj = queryString.parse(search);

        Object.entries(obj)
          .filter((item) => whiteListedKeys.includes(item[0]))
          .forEach((entry) =>
            this.updateFormState(
              PAYMENT_FORMS.partner,
              entry[0],
              entry[1],
              false
            )
          );

        callback();
      }
    },
    getFaqItemClass(id) {
      const className = "faq-list-item";

      return classnames(className, {
        ["is-active"]: this.touched[id],
      });
    },
    handleTouchItem(e, id) {
      if (e) {
        e.preventDefault();
      }

      this.touched[id] = !this.touched[id];
    },
    trackEmail() {
      if (window?.analytics) {
        window.analytics.track("New user - Ask for email", {
          courseType: getCourseType(this.onboardingData.choose_path),
          Host: "Web",
        });
      }
    },
    getPlans() {
      const plans = this.allPlans;

      return plans;
    },
    planDescription(plan) {
      const { billing_units, price } = plan;

      if (billing_units === PLAN_BILLING_UNITS.yearly) {
        const headsetPrice = this.calculateHeadsetPrice(plan);

        const total = (Number(price.amount) - headsetPrice) / 12;

        return `Equivalent to $${total.toFixed(2)} USD/month`;
      }

      return null;
    },
    calculateHeadsetPrice(plan) {
      const total = Number(plan.first_month_price.amount);
      const payment = Number(plan.price.amount);

      const calc = total - payment;

      return calc;
    },
    headsetPricingSummary(plan) {
      const { price } = plan;

      const headsetPrice = this.calculateHeadsetPrice(plan);

      if (headsetPrice > 0) {
        return `VR Headset $${headsetPrice} ${price.name} + shipping costs`;
      }

      return `+ Free VR headset + shipping costs`;
    },
    formatPlanTitle(plan) {
      const { billing_units, price } = plan;

      return `$${Number(price.amount)} ${
        billing_units === PLAN_BILLING_UNITS.yearly
          ? "USD Annually"
          : "USD Monthly"
      }`;
    },
    handleAction(e) {
      if (e) {
        e.preventDefault();
      }

      this.registerUserIsLoading = true;
      this.registerUser();
    },
    formIsValid() {
      const keys = Object.keys(this.formData);

      const properties = (validator, key) =>
        Object.prototype.hasOwnProperty.call(validator, key);

      const formIsValid = !keys.some((key) => {
        if (properties(this.validator, key)) {
          return !this.validator[key](this.formData[key].value).valid;
        }

        return false;
      });

      return formIsValid;
    },
    registerUser() {
      const { dispatch } = this.$store;
      const { choose_gender, choose_phobias } = this.onboardingData;
      const { email, name } = this.formData;
      const { action } = this;

      const payload = {
        email: email.value,
        gender: choose_gender,
        category_id: choose_phobias,
        first_name: name.value,
        course_type: "full",
        paymentType: "premium",
      };

      dispatch(`${MODULES.USER}/registerUser`, payload)
        .then((responseData) => {
          if (responseData?.programmes?.some((prog) => prog.plan_id)) {
            const error = "Error: You already have an account setup.";
            this.trackUserError({ message: error });

            return;
          }

          dispatch(`${MODULES.PAYMENT}/getSaleSummaryAction`, {
            email: payload.email,
            paymentType: payload.paymentType,
            ...denormaliseOrderSummary(this.formData),
            voucher_code: this.formData.promoApplied.value,
          })
            .then(() => this.identifyNewUserEvent(responseData))
            .then(() => action(this.formData.email.value))
            .then(() => (this.registerUserIsLoading = false));
        })
        .catch((e) => {
          this.registerUserIsLoading = false;
          this.trackUserError(e);
        });
    },
    trackUserError(error) {
      this.setError(error?.message || error?.error);
      this.registerUserIsLoading = false;

      if (window?.analytics && window?.analytics?.track) {
        window.analytics.track("New user - Has an account already", {
          Host: "Web",
          type: error?.message || error?.error,
        });
      }
    },
    identifyNewUserEvent(responseData) {
      if (
        responseData?.id &&
        window?.analytics &&
        window?.analytics?.identify &&
        window?.analytics?.track
      ) {
        try {
          const { plan_id } = this.formData;
          const { email, id } = responseData;

          const planInfo = this.getPlans().find(
            (plan) => plan_id.value === plan.id
          );

          if (!planInfo) {
            return;
          }

          window.analytics.identify(
            id,
            {
              email,
              subscriptionPeriod: planInfo?.billing_units,
              currency: (planInfo.first_month_price || planInfo.price).name,
            },
            {},
            () => {
              window.analytics.track("New User - Account Created", {
                Host: "Web",
                subscriptionPeriod: planInfo?.billing_units,
                currency: (planInfo.first_month_price || planInfo.price).name,
                planValue: (planInfo.first_month_price || planInfo.price)
                  .amount,
              });

              this.updateFormState(PAYMENT_FORMS.partner, "user_id", id, false);
            }
          );
        } catch (err) {
          console.error(err);
        }
      }
    },
  },
};
</script>
